'use client';

import { FC, useContext, useMemo } from 'react';
import { ButtonGroup, Flex, Button } from '@/_components';
import styles from './UnreadThreadIndicator.module.css';
import { UserContext } from '@/_contexts/UserProvider';
import type { Message } from '@/_types/models/conversations';
import type { UnreadThreadIndicatorProps } from './UnreadThreadIndicator.types';

export const UnreadThreadIndicator: FC<UnreadThreadIndicatorProps> = ({
  conversation,
}) => {
  const { user } = useContext(UserContext);

  const threadData = useMemo(() => {
    const messages: Array<Message> = conversation?.messages ?? [];
    const latestMessage = messages[messages.length - 1];
    const unreadThreadMessages = messages.filter(
      (m) => m.message_thread_id && m.unseen_user_ids?.includes(user?.id!),
    );

    return {
      unreadCount: unreadThreadMessages.length,
      targetThreadId: unreadThreadMessages.length
        ? unreadThreadMessages[unreadThreadMessages.length - 1]
            .message_thread_id
        : latestMessage?.message_thread_id,
      shouldShow:
        unreadThreadMessages.length > 0 || !!latestMessage?.message_thread_id,
    };
  }, [conversation?.messages, user?.id]);

  const onUnreadClick = (): void => {
    const element = document.getElementById(
      `message-${threadData.targetThreadId}`,
    );
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (
    !threadData.shouldShow ||
    typeof conversation?.id !== 'number' ||
    typeof user?.id !== 'number'
  ) {
    return null;
  }

  return (
    <Flex className={styles.unreadThreadIndicator} onClick={onUnreadClick}>
      <ButtonGroup>
        <Button
          value={
            threadData.unreadCount > 0
              ? 'Scroll to unread thread'
              : 'Scroll to latest message'
          }
          intent="secondary"
          size="small"
          notificationCount={threadData.unreadCount || 0}
        />
      </ButtonGroup>
    </Flex>
  );
};
